import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';
import { Snackbar } from '@dropbox/dig-components/snackbar';
import { Button } from '@dropbox/dig-components/buttons';
import { UIIcon } from '@dropbox/dig-icons';
import { OpenLine } from '@dropbox/dig-icons/assets';
import { PAP_View_DropboxIntegrationConfirmation } from '@dropbox/pap-events/sign_standalone/view_dropbox_integration_confirmation';
import type { ConfirmationType } from '@dropbox/pap-events/enums/confirmation_type';
import { useLogPAPEvent } from 'hellospa/common/use-log-pap-event';
import { Tone } from 'hellospa/components/snackbar/tone';
import { useSignAppClient } from 'js/sign-components/sign-app-client/context';
import {
  areAllToursSuppressed,
  useIsTourCompleted,
} from 'js/sign-components/tour/useTour';
import { ToursContext } from 'js/sign-components/sign-app-client/tours-provider';
import type { TourName } from 'js/sign-components/common/TourName';

const onboardingModals: TourName[] = [
  'fss-initial-onboarding',
  'fss-sr-onboarding-first',
  'fss-sr-onboarding-second',
  'fss-sr-onboarding-third',
];

export type DropboxIntegrationConfirmation = {
  folderName: string;
  folderUrl: string;
};

const ConfirmationContext = createContext({
  hasConfirmation: false,
  showInModal: false,
  onModalViewed: () => {},
  onModalClosed: () => {},
  onModalAction: () => {},
});

export const useDropboxIntegrationConfirmation = () =>
  useContext(ConfirmationContext);

function Confirmation({
  folderName,
  folderUrl,
  children,
}: React.PropsWithChildren<DropboxIntegrationConfirmation>) {
  const { tours } = useSignAppClient();
  const { pathname } = useLocation();
  const isChoosingPlan =
    pathname.replace(/\/$/, '') === '/info/chooseSignupPlan';
  const initialDestination = useRef<string | null>(null);
  const { activeTour, pendingTourChecks } = ToursContext.useContext();
  const modalOpen =
    activeTour !== null && onboardingModals.includes(activeTour);
  const modalPending = onboardingModals.some(
    (tour) => (pendingTourChecks[tour] ?? 0) > 0,
  );
  const completed = useIsTourCompleted('fss-initial-onboarding');
  const [mode, setMode] = useState<'pending' | 'toast' | 'modal'>('pending');
  const [open, setOpen] = useState(true);
  const viewed = useRef(new Set<ConfirmationType>());
  const logPAPEvent = useLogPAPEvent();

  const hasLeftDestination =
    initialDestination.current !== null &&
    initialDestination.current !== pathname;

  useEffect(() => {
    if (
      modalOpen &&
      activeTour !== 'fss-initial-onboarding' &&
      activeTour !== 'fss-sr-onboarding-first'
    ) {
      setOpen(false);
    }
  }, [modalOpen, activeTour]);

  useEffect(() => {
    if (initialDestination.current === null) {
      if (!isChoosingPlan) initialDestination.current = pathname;
    } else if (initialDestination.current !== pathname) {
      setOpen(false);
    }
  }, [pathname, isChoosingPlan]);

  useEffect(() => {
    if (mode !== 'pending' || isChoosingPlan || !open) return;
    const isHome = ['/', '/home/index', '/home/chooseSignMethod'].includes(
      pathname.replace(/\/$/, '') || '/',
    );
    if (!isHome) {
      setMode('toast');
      return;
    }
    if (completed || areAllToursSuppressed()) {
      setMode('toast');
      return;
    }

    let cancelled = false;
    tours.isEligibleForTour('fss-initial-onboarding').then(
      (eligible) => {
        if (!cancelled) setMode(eligible ? 'modal' : 'toast');
      },
      () => {
        if (!cancelled) setMode('toast');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [mode, completed, tours, pathname, isChoosingPlan, open]);

  const trackView = useCallback(
    (confirmationType: ConfirmationType) => {
      if (viewed.current.has(confirmationType)) return;
      viewed.current.add(confirmationType);
      logPAPEvent(
        PAP_View_DropboxIntegrationConfirmation({
          site: 'sign',
          updateTrigger: 'auto_account_creation',
          signTrigger: 'initial_connection',
          confirmationType,
        }),
      );
    },
    [logPAPEvent],
  );

  const onModalViewed = useCallback(() => trackView('modal_copy'), [trackView]);
  const onModalClosed = useCallback(() => setMode('toast'), []);
  const onModalAction = useCallback(() => setOpen(false), []);
  const showToast =
    mode === 'toast' &&
    open &&
    !modalOpen &&
    !modalPending &&
    !isChoosingPlan &&
    !hasLeftDestination;

  useEffect(() => {
    if (showToast) trackView('toast');
  }, [showToast, trackView]);

  return (
    <ConfirmationContext.Provider
      value={{
        hasConfirmation: open && !hasLeftDestination,
        showInModal: mode === 'modal' && open && !hasLeftDestination,
        onModalViewed,
        onModalClosed,
        onModalAction,
      }}
    >
      {children}
      {showToast && (
        <Snackbar.Position>
          <Snackbar
            open={open}
            onRequestClose={() => setOpen(false)}
            timeout={10000}
            preferComposition
            role="status"
            data-testid="dropbox-integration-confirmation"
          >
            <Tone tone="success" />
            <Snackbar.Content>
              <Snackbar.Message>
                <FormattedMessage
                  id=""
                  defaultMessage="Your Dropbox integration is ready to use. Find your {folderName} folder in Dropbox."
                  description="Confirmation after Dropbox integration is automatically enabled, explaining where to find the synced documents"
                  values={{ folderName }}
                />
              </Snackbar.Message>
              <Snackbar.Actions>
                <Button
                  variant="transparent"
                  inverse
                  withIconStart={<UIIcon src={OpenLine} />}
                  href={folderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                >
                  <FormattedMessage
                    id=""
                    defaultMessage="Open folder"
                    description="Button that opens the Dropbox folder containing documents synced from the signing workspace"
                  />
                </Button>
              </Snackbar.Actions>
            </Snackbar.Content>
          </Snackbar>
        </Snackbar.Position>
      )}
    </ConfirmationContext.Provider>
  );
}

export function DropboxIntegrationConfirmationProvider({
  confirmation,
  children,
}: React.PropsWithChildren<{
  confirmation?: DropboxIntegrationConfirmation | null;
}>) {
  return confirmation ? (
    <Confirmation {...confirmation}>{children}</Confirmation>
  ) : (
    <>{children}</>
  );
}
